import React, { useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { useMapSearch } from '@hero/web-hooks';
import { MapSearchProps } from './types';
import { MapSearchInput } from './MapSearchInput';
import { MapSearchDropdown } from './MapSearchDropdown';
import { SavedLocationsTabs } from './SavedLocationsTabs';
import { SearchResultsList } from './SearchResultsList';

/**
 * MapSearch component for searching locations
 * Supports 'google-only' and 'google-and-saved-locations' modes
 */
export const MapSearch = React.forwardRef<
  { handleCoordinates: (lat: number, lng: number, index: number) => Promise<void>; isClosingDropdownRef: React.MutableRefObject<boolean> },
  MapSearchProps
>((props, ref) => {
  const { t: tOriginal, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  // Wrapper function to match expected signature
  const t = (key: string, defaultValue?: string): string => {
    return tOriginal(key, { defaultValue: defaultValue || key });
  };

  // All logic in hook
  const mapSearch = useMapSearch(props);

  // Expose via ref
  useImperativeHandle(ref, () => ({
    handleCoordinates: mapSearch.handleCoordinates,
    isClosingDropdownRef: mapSearch.isClosingDropdownRef,
  }));

  if (!props.showSearch) return null;

  return (
    <div className="absolute top-4 left-4 right-4 z-30" ref={mapSearch.containerRef}>
      <MapSearchInput
        value={mapSearch.inputState.searchTerm}
        placeholder={mapSearch.inputState.placeholder}
        onChange={mapSearch.handleSearchChange}
        onFocus={mapSearch.handleInputFocus}
        onBlur={mapSearch.handleInputBlur}
        onKeyDown={mapSearch.handleKeyDown}
        onClear={mapSearch.handleClear}
        onGetCurrentLocation={mapSearch.handleGetCurrentLocation}
        isLoading={mapSearch.isLoading}
        isLoadingCoordinates={mapSearch.isLoadingCoordinates}
        isGettingCurrentLocation={mapSearch.isGettingCurrentLocation}
        isRTL={isRTL}
        showClear={!!(mapSearch.inputState.searchTerm || mapSearch.inputState.placeholder)}
        searchInputRef={mapSearch.searchInputRef}
        t={t}
      />

      {mapSearch.uiState.showResults && (
        <MapSearchDropdown
          showTabs={mapSearch.shouldShowTabs}
          showSearchResults={mapSearch.shouldShowSearchResults}>
          {mapSearch.shouldShowTabs && (
            <SavedLocationsTabs
              activeTab={mapSearch.uiState.activeTab}
              onTabChange={mapSearch.setActiveTab}
              myPlaces={mapSearch.categorizedSavedLocations.myPlaces}
              clientPlaces={mapSearch.categorizedSavedLocations.clientPlaces}
              isLoadingMyPlaces={mapSearch.isLoadingMyPlaces}
              isLoadingClientPlaces={mapSearch.isLoadingClientPlaces}
              hasNextMyPlaces={mapSearch.hasNextMyPlaces}
              hasNextClientPlaces={mapSearch.hasNextClientPlaces}
              isFetchingNextMyPlaces={mapSearch.isFetchingNextMyPlaces}
              isFetchingNextClientPlaces={mapSearch.isFetchingNextClientPlaces}
              onLoadMoreMyPlaces={mapSearch.onLoadMoreMyPlaces}
              onLoadMoreClientPlaces={mapSearch.onLoadMoreClientPlaces}
              onSelectLocation={mapSearch.handleSelectSavedLocation}
              isRTL={isRTL}
              t={t}
            />
          )}
          {mapSearch.shouldShowSearchResults && (
            <SearchResultsList
              googlePlaces={mapSearch.googlePlaces}
              savedLocations={mapSearch.savedLocations}
              searchMode={props.searchMode || 'google-and-saved-locations'}
              onSelectGooglePlace={mapSearch.handleSelectGooglePlace}
              onSelectSavedLocation={mapSearch.handleSelectSavedLocation}
              isRTL={isRTL}
              t={t}
            />
          )}
        </MapSearchDropdown>
      )}
    </div>
  );
});

MapSearch.displayName = 'MapSearch';
